import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { hasPermission } from '../utils/roles';
import { assetAPI, employeeAPI } from '../services/api';
import Modal from './Modal';
import './AssetManagement.css';

const AssetManagement = () => {
  const { currentUser } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [viewAsset, setViewAsset] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const canCreate = hasPermission(currentUser?.role, 'employee_create');
  const canEdit = hasPermission(currentUser?.role, 'employee_edit');
  const canDelete = hasPermission(currentUser?.role, 'employee_delete');

  const [assets, setAssets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await assetAPI.getAll();
        setAssets(res?.data || res || []);
      } catch (e) { setApiError('Failed to load assets'); }
      finally { setLoading(false); }
    };
    fetchData();
    if (canCreate || canEdit) {
      employeeAPI.getAll().then(r => setEmployees(r?.data || r || [])).catch(() => {});
    }
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    type: 'Laptop',
    serialNumber: '',
    purchaseDate: '',
    price: '',
    status: 'available',
    assignedTo: '',
    condition: 'New',
    warranty: ''
  });

  const assetTypes = ['Laptop', 'Mobile', 'Tablet', 'Monitor', 'Accessory', 'Equipment', 'Furniture', 'Other'];
  const statusOptions = ['available', 'assigned', 'under-repair', 'retired'];
  const conditionOptions = ['New', 'Excellent', 'Good', 'Fair', 'Poor'];

  const getStatusBadge = (status) => {
    const badges = {
      available: { class: 'badge-success', icon: 'fa-check-circle', text: 'Available' },
      assigned: { class: 'badge-info', icon: 'fa-user', text: 'Assigned' },
      'under-repair': { class: 'badge-warning', icon: 'fa-tools', text: 'Under Repair' },
      retired: { class: 'badge-danger', icon: 'fa-times-circle', text: 'Retired' }
    };
    return badges[status] || badges.available;
  };

  const getTypeIcon = (type) => {
    const icons = {
      Laptop: 'fa-laptop',
      Mobile: 'fa-mobile-alt',
      Tablet: 'fa-tablet-alt',
      Monitor: 'fa-desktop',
      Accessory: 'fa-mouse',
      Equipment: 'fa-tools',
      Furniture: 'fa-chair',
      Other: 'fa-box'
    };
    return icons[type] || 'fa-box';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await assetAPI.create(formData);
      const res = await assetAPI.getAll();
      setAssets(res?.data || res || []);
      setShowModal(false);
      setFormData({ name: '', type: 'Laptop', serialNumber: '', purchaseDate: '', price: '', status: 'available', assignedTo: '', condition: 'New', warranty: '' });
      showNotification('Asset added successfully!', 'success');
    } catch (err) {
      showNotification('Failed to add asset', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!canDelete) {
      alert('You do not have permission to delete assets');
      return;
    }
    if (window.confirm('Are you sure you want to delete this asset?')) {
      try {
        await assetAPI.delete(id);
        setAssets(assets.filter(asset => (asset._id || asset.id) !== id));
        showNotification('Asset deleted successfully', 'success');
      } catch (e) { showNotification('Failed to delete asset', 'error'); }
    }
  };

  const showNotification = (message, type = 'info') => {
    const notification = document.createElement('div');
    notification.className = `notification ${type}`;
    notification.innerHTML = `
      <i class="fas fa-${type === 'success' ? 'check-circle' : 'info-circle'}"></i>
      <span>${message}</span>
    `;
    notification.style.cssText = `
      position: fixed;
      top: 90px;
      right: 30px;
      background: ${type === 'success' ? '#10b981' : '#3b82f6'};
      color: white;
      padding: 15px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      display: flex;
      align-items: center;
      gap: 10px;
      z-index: 10000;
      animation: slideInRight 0.3s ease;
    `;
    document.body.appendChild(notification);
    setTimeout(() => {
      notification.style.animation = 'slideOutRight 0.3s ease';
      setTimeout(() => document.body.removeChild(notification), 300);
    }, 3000);
  };

  const filteredAssets = assets.filter(asset => {
    const matchesStatus = filterStatus === 'all' || asset.status === filterStatus;
    const matchesType = filterType === 'all' || asset.type === filterType;
    const assignedName = asset.assignedTo?.name || asset.assignedToName || '';
    const matchesSearch = (asset.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (asset.serialNumber || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         assignedName.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesType && matchesSearch;
  });

  const stats = {
    total: assets.length,
    available: assets.filter(a => a.status === 'available').length,
    assigned: assets.filter(a => a.status === 'assigned').length,
    underRepair: assets.filter(a => a.status === 'under-repair').length
  };

  return (
    <div className="asset-management">
      {apiError && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 16px', borderRadius: 8, marginBottom: 16 }}>{apiError}</div>}
      <div className="module-header">
        <h2><i className="fas fa-box"></i> Asset Management</h2>
        <div className="header-actions">
          {canCreate && (
            <button className="btn btn-primary" onClick={() => setShowModal(true)}>
              <i className="fas fa-plus"></i> Add Asset
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card blue">
          <div className="stat-icon">
            <i className="fas fa-box"></i>
          </div>
          <div className="stat-info">
            <h3>{stats.total}</h3>
            <p>Total Assets</p>
          </div>
        </div>
        <div className="stat-card green">
          <div className="stat-icon">
            <i className="fas fa-check-circle"></i>
          </div>
          <div className="stat-info">
            <h3>{stats.available}</h3>
            <p>Available</p>
          </div>
        </div>
        <div className="stat-card info">
          <div className="stat-icon">
            <i className="fas fa-user"></i>
          </div>
          <div className="stat-info">
            <h3>{stats.assigned}</h3>
            <p>Assigned</p>
          </div>
        </div>
        <div className="stat-card orange">
          <div className="stat-icon">
            <i className="fas fa-tools"></i>
          </div>
          <div className="stat-info">
            <h3>{stats.underRepair}</h3>
            <p>Under Repair</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="search-box">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Search assets, serial number, or employee..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Status</option>
            <option value="available">Available</option>
            <option value="assigned">Assigned</option>
            <option value="under-repair">Under Repair</option>
            <option value="retired">Retired</option>
          </select>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="all">All Types</option>
            {assetTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Assets Table */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Asset ID</th>
              <th>Asset Name</th>
              <th>Type</th>
              <th>Serial Number</th>
              <th>Status</th>
              <th>Assigned To</th>
              <th>Department</th>
              <th>Condition</th>
              <th>Value</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="10" style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}><i className="fas fa-spinner fa-spin"></i> Loading...</td></tr>
            ) : filteredAssets.length === 0 ? (
              <tr><td colSpan="10" style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>No assets found</td></tr>
            ) : filteredAssets.map((asset) => {
              const badge = getStatusBadge(asset.status);
              return (
                <tr key={asset._id || asset.id}>
                  <td>
                    <strong>{asset.assetId || asset.id}</strong>
                  </td>
                  <td>
                    <div className="asset-info">
                      <i className={`fas ${getTypeIcon(asset.type)}`}></i>
                      <span>{asset.name}</span>
                    </div>
                  </td>
                  <td>
                    <span className="type-badge">{asset.type}</span>
                  </td>
                  <td className="serial-number">{asset.serialNumber}</td>
                  <td>
                    <span className={`badge ${badge.class}`}>
                      <i className={`fas ${badge.icon}`}></i>
                      {badge.text}
                    </span>
                  </td>
                  <td>
                    {(asset.assignedTo?.name || asset.assignedToName) ? (
                      <div className="assigned-info">
                        <i className="fas fa-user"></i>
                        {asset.assignedTo?.name || asset.assignedToName}
                      </div>
                    ) : (
                      <span className="text-muted">Not Assigned</span>
                    )}
                  </td>
                  <td>{asset.assignedTo?.department || asset.department || '-'}</td>
                  <td>
                    <span className={`condition-badge condition-${(asset.condition || 'good').toLowerCase()}`}>
                      {asset.condition}
                    </span>
                  </td>
                  <td className="price">${(asset.price || 0).toLocaleString()}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon view"
                        onClick={() => setViewAsset(asset)}
                        title="View Details"
                      >
                        <i className="fas fa-eye"></i>
                      </button>
                      {canEdit && (
                        <button className="btn-icon edit" title="Edit">
                          <i className="fas fa-edit"></i>
                        </button>
                      )}
                      {canDelete && (
                        <button
                          className="btn-icon delete"
                          onClick={() => handleDelete(asset._id || asset.id)}
                          title="Delete"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Add Asset Modal */}
      <Modal show={showModal} onClose={() => setShowModal(false)} title="Add New Asset">
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label>Asset Name *</label>
              <input
                type="text"
                name="name"
                className="form-input"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="e.g., MacBook Pro 16"
              />
            </div>

            <div className="form-group">
              <label>Asset Type *</label>
              <select
                name="type"
                className="form-input"
                value={formData.type}
                onChange={handleInputChange}
                required
              >
                {assetTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Serial Number *</label>
              <input
                type="text"
                name="serialNumber"
                className="form-input"
                value={formData.serialNumber}
                onChange={handleInputChange}
                required
                placeholder="e.g., MBP2023X1234"
              />
            </div>

            <div className="form-group">
              <label>Purchase Date *</label>
              <input
                type="date"
                name="purchaseDate"
                className="form-input"
                value={formData.purchaseDate}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Price (₹) *</label>
              <input
                type="number"
                name="price"
                className="form-input"
                value={formData.price}
                onChange={handleInputChange}
                required
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label>Condition *</label>
              <select
                name="condition"
                className="form-input"
                value={formData.condition}
                onChange={handleInputChange}
                required
              >
                {conditionOptions.map(cond => (
                  <option key={cond} value={cond}>{cond}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Warranty Expiry</label>
              <input
                type="date"
                name="warranty"
                className="form-input"
                value={formData.warranty}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label>Status *</label>
              <select
                name="status"
                className="form-input"
                value={formData.status}
                onChange={handleInputChange}
                required
              >
                {statusOptions.map(status => (
                  <option key={status} value={status}>
                    {status.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                  </option>
                ))}
              </select>
            </div>

            {formData.status === 'assigned' && (
              <div className="form-group">
                <label>Assign To Employee</label>
                <select
                  name="assignedTo"
                  className="form-input"
                  value={formData.assignedTo}
                  onChange={handleInputChange}
                >
                  <option value="">Select Employee</option>
                  {employees.map(emp => (
                    <option key={emp._id} value={emp._id}>{emp.name} ({emp.employeeId})</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Add Asset
            </button>
          </div>
        </form>
      </Modal>

      {/* View Asset Details Modal */}
      <Modal show={viewAsset !== null} onClose={() => setViewAsset(null)} title="Asset Details">
        {viewAsset && (
          <div className="asset-details">
            <div className="detail-header">
              <i className={`fas ${getTypeIcon(viewAsset.type)} detail-icon`}></i>
              <div>
                <h3>{viewAsset.name}</h3>
                <span className={`badge ${getStatusBadge(viewAsset.status).class}`}>
                  <i className={`fas ${getStatusBadge(viewAsset.status).icon}`}></i>
                  {getStatusBadge(viewAsset.status).text}
                </span>
              </div>
            </div>

            <div className="detail-section">
              <h4><i className="fas fa-info-circle"></i> Asset Information</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Asset ID:</label>
                  <span>{viewAsset.id}</span>
                </div>
                <div className="detail-item">
                  <label>Type:</label>
                  <span>{viewAsset.type}</span>
                </div>
                <div className="detail-item">
                  <label>Serial Number:</label>
                  <span>{viewAsset.serialNumber}</span>
                </div>
                <div className="detail-item">
                  <label>Condition:</label>
                  <span className={`condition-badge condition-${viewAsset.condition.toLowerCase()}`}>
                    {viewAsset.condition}
                  </span>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h4><i className="fas fa-rupee-sign"></i> Financial Information</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Purchase Date:</label>
                  <span>{new Date(viewAsset.purchaseDate).toLocaleDateString()}</span>
                </div>
                <div className="detail-item">
                  <label>Purchase Price:</label>
                  <span className="price-highlight">₹{viewAsset.price.toLocaleString()}</span>
                </div>
                <div className="detail-item">
                  <label>Warranty Expiry:</label>
                  <span>{new Date(viewAsset.warranty).toLocaleDateString()}</span>
                </div>
              </div>
            </div>

            {viewAsset.assignedTo && (
              <div className="detail-section">
                <h4><i className="fas fa-user"></i> Assignment Information</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Assigned To:</label>
                    <span>{viewAsset.assignedToName}</span>
                  </div>
                  <div className="detail-item">
                    <label>Employee ID:</label>
                    <span>{viewAsset.assignedTo}</span>
                  </div>
                  <div className="detail-item">
                    <label>Department:</label>
                    <span>{viewAsset.department}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AssetManagement;
